import { useState, useMemo } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AdminDataTable,
  AdminModal,
  type ColumnDef,
  EmptyState,
} from "@/components/admin/shared";
import { MovementTypeBadge } from "./MovementTypeBadge";
import { useStockMovements } from "@/hooks/admin/useInventory";
import type { InventoryProduct, StockMovement } from "@/lib/admin-api/inventory";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MovementHistoryModalProps {
  readonly product: InventoryProduct | null;
  readonly onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MovementHistoryModal({
  product,
  onClose,
}: MovementHistoryModalProps) {
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({
      product_id: product?.id ?? null,
      page,
      limit: 15,
    }),
    [product?.id, page],
  );

  const { data, isLoading, isError } = useStockMovements(params, { enabled: product !== null });

  const movements = data?.movements ?? [];
  const pagination = data?.pagination;

  const columns: readonly ColumnDef<StockMovement>[] = [
    {
      id: "date",
      header: "Date",
      cell: (m) => (
        <span className="text-xs text-stone-500">
          {format(new Date(m.created_at), "dd MMM yyyy HH:mm", { locale: fr })}
        </span>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: (m) => <MovementTypeBadge type={m.type} />,
    },
    {
      id: "quantity",
      header: "Qté",
      cell: (m) => {
        const isPositive = ["restock", "return"].includes(m.type);
        return (
          <span
            className={`tabular-nums font-semibold ${
              isPositive ? "text-emerald-700" : "text-red-600"
            }`}
          >
            {isPositive ? "+" : "-"}
            {Math.abs(m.quantity)}
          </span>
        );
      },
    },
    {
      id: "reason",
      header: "Raison",
      mobileVisible: false,
      cell: (m) => (
        <span className="text-sm text-stone-600">{m.reason}</span>
      ),
    },
    {
      id: "operator",
      header: "Opérateur",
      mobileVisible: false,
      cell: (m) => (
        <span className="text-xs text-stone-500">{m.operator}</span>
      ),
    },
  ];

  if (!product) return null;

  return (
    <AdminModal
      open={!!product}
      onClose={onClose}
      title={`Historique — ${product.name}`}
      size="lg"
    >
      <div className="space-y-3">
        {/* Product info pill */}
        <div className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1">
          <span className="font-mono text-xs text-stone-500">{product.sku}</span>
          <span className="text-xs text-stone-400">·</span>
          <span className="text-xs font-medium text-stone-700">
            Stock actuel : {product.current_stock}
          </span>
        </div>

        {/* Error */}
        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Impossible de charger l&apos;historique.
          </div>
        )}

        {/* Table */}
        {!isLoading && movements.length === 0 ? (
          <EmptyState
            title="Aucun mouvement"
            description="Aucun mouvement enregistré pour ce produit."
          />
        ) : (
          <AdminDataTable<StockMovement>
            columns={columns}
            data={movements}
            isLoading={isLoading}
            pagination={pagination}
            onPageChange={setPage}
            emptyMessage="Aucun mouvement"
          />
        )}
      </div>
    </AdminModal>
  );
}
